import { Button, Modal } from 'antd';
import { Style } from 'geostyler-style';
import { parseFromGeoStyleToColorSteps } from 'utils';
import { arrayMove, List } from 'react-movable';
import { useEffect, useState } from 'react';
import { ColorStep } from 'types/app.types';
import { HolderOutlined, PlusOutlined } from '@ant-design/icons';
import ColorScale from '../../../ColorScale';
import ColorSelector from './ components/ColorSelector';
import styles from './index.module.scss';
import SaveAsModal from './ components/SaveAsModal';

const {
  titleWrapper, colorsWrapper, item, more,
} = styles;

interface ColorScaleEditorModalProps {
    open: boolean,
    onOk: (colors: ColorStep[], value?: string) => void,
    onCancel: () => void,
    style?: Style
}

function ColorScaleEditorModal({
  open, onOk, onCancel, style,
} : ColorScaleEditorModalProps) {
  const currentStyle = style as Style;
  const [showSaveAsModal, setShowSaveAsModal] = useState<boolean>(false);
  const [colors, setColors] = useState<ColorStep[]>(currentStyle
    ? parseFromGeoStyleToColorSteps(currentStyle) : []);

  const handleOnColorSelectorChange = (colorStep : ColorStep, index: number) => {
    setColors((prev) => {
      const c = [...prev];
      c[index] = colorStep;
      return c;
    });
  };

  const handleOnColorStepDelete = (index:number) => {
    setColors((prev) => {
      const c = [...prev];
      c.splice(index, 1);
      return c;
    });
  };

  const handleOnAddButtonClick = () => {
    setColors((prev) => [...prev, {
      color: '#FFFFFF',
      threshold: 0,
      opacity: 0,
    }] as ColorStep[]);
  };

  useEffect(() => {
    setColors(parseFromGeoStyleToColorSteps(style));
  }, [style]);

  return (
    <>
      <Modal
        title="Styles editor"
        open={open}
        destroyOnClose
        onCancel={onCancel}
        footer={(
          <>
            <Button onClick={() => setShowSaveAsModal(true)}>Save as...</Button>
            <Button type="primary" onClick={() => onOk(colors)}>Save</Button>
            <Button onClick={onCancel}>Cancel</Button>
          </>
)}
      >
        <div className={titleWrapper}>
          <h2>{currentStyle?.name}</h2>
          <ColorScale colors={colors} gradient />
        </div>
        <div className={colorsWrapper}>
          <List
            values={colors}
            onChange={({ oldIndex, newIndex }) => {
              setColors((prev: ColorStep[]) => arrayMove(prev, oldIndex, newIndex));
            }}
            renderList={({ children, props }) => (
              <ul
                {...props}
                style={{
                  listStyle: 'none',
                  paddingLeft: 0,
                  maxHeight: '50vh',
                  overflow: 'scroll',
                }}
              >
                {children}
              </ul>
            )}
            renderItem={({
              value, props, isDragged, index,
            }) => (
              <li
                {...props}
                style={{
                  ...props.style,
                  cursor: isDragged ? 'grabbing' : 'grab',
                  zIndex: 1500,
                }}
                className={`${item}`}
              >
                <HolderOutlined data-movable-handle />
                <ColorSelector
                  colorStep={value}
                  onChange={(colorStep) => handleOnColorSelectorChange(colorStep, index || 0)}
                  onDelete={() => handleOnColorStepDelete(index || 0)}
                />
              </li>
            )}
          />
          <div className={more}>
            <Button type="default" icon={<PlusOutlined />} onClick={handleOnAddButtonClick}>Add color step</Button>
          </div>
        </div>
      </Modal>
      {showSaveAsModal && (
      <SaveAsModal
        open={showSaveAsModal}
        onCancel={() => setShowSaveAsModal((prev) => !prev)}
        onOk={(value) => {
          setShowSaveAsModal((prev) => !prev);
          if (value) { onOk(colors, value); }
        }}
      />
      )}
    </>
  );
}

export default ColorScaleEditorModal;
